import ImageThumbnail from '@/components/media/ImageThumbnail'
import ProductStorefrontVisibilityBadge from '@/components/products/ProductStorefrontVisibilityBadge'
import ActionsMenu from '@/components/tables/ActionsMenu'
import DeleteActionWithConfirmation from '@/components/tables/DeleteActionWithConfirmation'
import { Badge } from '@/components/ui/badge'
import { Typography } from '@/components/ui/typography'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can, useAbility } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import { ProductStorefrontVisibility } from '@/lib/enums/ProductStorefrontVisibility'
import { isSubscriptionPackage } from '@/lib/product-catalog-type'
import {
  getPriceAdjustmentActionLabel,
  getPriceAdjustmentScheduleSummary,
  getPriceAdjustmentSignedAmountLabel,
} from '@/lib/services/price-adjustments/priceAdjustments.summary'
import { useSoftDeleteProductMutation } from '@/lib/services/products/products.query'
import { formatCurrency, getEnumDisplayName, getErrorMessage } from '@/lib/utils'
import { Link } from '@tanstack/react-router'
import {
  CalendarClock,
  DollarSign,
  Eye,
  Flower2,
  LibraryBig,
  Layers3,
  PackagePlus,
  Sparkles,
} from 'lucide-react'
import { toast } from 'sonner'

export const productIndexSortColumns = {
  product: {
    asc: 'name_asc',
    desc: 'name_desc',
    defaultSort: 'name_asc',
  },
  storefrontVisibility: {
    asc: 'storefront_visibility_asc',
    desc: 'storefront_visibility_desc',
    defaultSort: 'storefront_visibility_asc',
  },
  variants: {
    asc: 'variants_asc',
    desc: 'variants_desc',
    defaultSort: 'variants_desc',
  },
  updated: {
    asc: 'updated_asc',
    desc: 'updated_desc',
    defaultSort: 'updated_desc',
  },
}

function ProductActionsContent({ product }) {
  return (
    <div className="flex justify-start lg:justify-end">
      <ActionsMenu row={{ original: product }}>
        <Can do="delete" on="products">
          {() => <ProductsDeleteAction />}
        </Can>
      </ActionsMenu>
    </div>
  )
}

function normalizeMoney(value) {
  const amount = Number(value)

  return Number.isFinite(amount) ? amount : null
}

function getProductVariants(product) {
  if (Array.isArray(product?.variants) && product.variants.length) {
    return product.variants
  }

  return product?.defaultVariant ? [product.defaultVariant] : []
}

function getVariantPrice(variant) {
  return normalizeMoney(variant?.effective_price ?? variant?.price)
}

function formatCompactCurrency(amount) {
  const formatted = formatCurrency(amount)

  return formatted.replace(/\.00$/, '')
}

function getProductPriceRange(product) {
  const prices = getProductVariants(product)
    .map(getVariantPrice)
    .filter((price) => price !== null)

  if (!prices.length) {
    return null
  }

  const lowestPrice = Math.min(...prices)
  const highestPrice = Math.max(...prices)

  if (lowestPrice === highestPrice) {
    return formatCompactCurrency(lowestPrice)
  }

  return `${formatCompactCurrency(lowestPrice)} - ${formatCompactCurrency(highestPrice)}`
}

function getActivePriceAdjustment(product) {
  return (
    getProductVariants(product)
      .map((variant) => variant?.active_price_adjustment)
      .find((adjustment) => adjustment?.id) ?? null
  )
}

function getPriceAdjustmentIndexBadgeLabel(adjustment) {
  if (adjustment?.amount !== undefined && adjustment?.amount !== null) {
    return getPriceAdjustmentSignedAmountLabel(adjustment)
  }

  return adjustment?.summary ?? 'Price change'
}

function getPriceAdjustmentIndexEndLabel(adjustment) {
  const activeWindowEnd = adjustment?.active_window?.ends_at ?? adjustment?.active_window_ends_at

  if (activeWindowEnd) {
    const parsedEnd = dayjs(activeWindowEnd)

    if (parsedEnd.isValid()) {
      return `ends ${parsedEnd.subtract(1, 'day').format('M/D')}`
    }
  }

  return null
}

function getPriceAdjustmentIndexTooltipScheduleLabel(adjustment) {
  const endLabel = getPriceAdjustmentIndexEndLabel(adjustment)

  if (endLabel) {
    return endLabel
  }

  const scheduleSummary = getPriceAdjustmentScheduleSummary(adjustment)

  if (typeof scheduleSummary === 'string' && scheduleSummary.trim()) {
    return scheduleSummary.replace(/^Active now\.\s*/i, '').replace(/\.$/, '')
  }

  return 'Active right now'
}

function getVariantCount(product) {
  return getProductVariants(product).length
}

function getCollectionNames(product) {
  const collections = Array.isArray(product?.collections) ? product.collections : []

  return collections.map((collection) => collection.name).filter(Boolean)
}

function getCollectionSummary(product) {
  const collectionNames = getCollectionNames(product)

  if (!collectionNames.length) {
    return 'No collections'
  }

  if (collectionNames.length <= 2) {
    return collectionNames.join(', ')
  }

  return `${collectionNames.slice(0, 2).join(', ')} +${collectionNames.length - 2}`
}

function ProductCollectionsContent({ product }) {
  const collectionNames = getCollectionNames(product)

  if (!collectionNames.length) {
    return (
      <Typography variant="supporting" italic>
        No collections
      </Typography>
    )
  }

  const shownCollectionNames = collectionNames.slice(0, 2)
  const hiddenCollectionCount = collectionNames.length - shownCollectionNames.length

  return (
    <div className="flex max-w-64 flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
      {shownCollectionNames.map((collectionName) => (
        <span key={collectionName} className="min-w-0 truncate">
          {collectionName}
        </span>
      ))}
      {hiddenCollectionCount > 0 ? (
        <Badge variant="muted" size="xs">
          +{hiddenCollectionCount}
        </Badge>
      ) : null}
    </div>
  )
}

function getProductKindBadgeMeta(product) {
  if (isSubscriptionPackage(product)) {
    return {
      icon: CalendarClock,
      label: 'Subscription package',
      variant: 'info',
    }
  }

  if (product?.is_add_on_only) {
    return {
      icon: PackagePlus,
      label: 'Add-on only',
      variant: 'brand',
    }
  }

  if (product?.is_add_on) {
    return {
      icon: PackagePlus,
      label: 'Product + Add-on',
      variant: 'brand',
    }
  }

  return null
}

function ProductMarkerBadges({ product }) {
  const kindBadge = getProductKindBadgeMeta(product)
  const badges = [
    product?.is_designers_choice
      ? {
          icon: Sparkles,
          label: "Designer's Choice",
          variant: 'brand',
        }
      : null,
    kindBadge,
  ].filter(Boolean)

  if (!badges.length) {
    return null
  }

  return (
    <div className="mt-1 flex flex-wrap gap-1.5">
      {badges.map((badge) => {
        const Icon = badge.icon

        return (
          <Badge
            key={badge.label}
            variant={badge.variant}
            size="xs"
            className="gap-1.5 [&>svg]:size-3"
          >
            <Icon aria-hidden="true" />
            {badge.label}
          </Badge>
        )
      })}
    </div>
  )
}

function ProductPriceChangeBadge({ adjustment }) {
  const path = usePathHelper()
  const ability = useAbility()

  if (!adjustment?.id) {
    return null
  }

  const label = getPriceAdjustmentIndexBadgeLabel(adjustment)
  const endLabel = getPriceAdjustmentIndexEndLabel(adjustment)
  const scheduleLabel = getPriceAdjustmentIndexTooltipScheduleLabel(adjustment)
  const badge = (
    <Badge variant="warning" size="xs" className="gap-1.5 px-2.5">
      <span className="size-1.5 rounded-full bg-warning" aria-hidden="true" />
      {label}
      {endLabel ? (
        <>
          <span className="text-muted-foreground" aria-hidden="true">
            &middot;
          </span>
          <span>{endLabel}</span>
        </>
      ) : null}
    </Badge>
  )
  const triggerClassName = 'inline-flex w-fit self-start lg:self-end'
  const trigger = ability.can('edit', 'products') ? (
    <Link
      {...path.priceChanges.show(adjustment.id).link}
      className={triggerClassName}
      aria-label={`${label}${endLabel ? ` ${endLabel}` : ''} active price change`}
    >
      {badge}
    </Link>
  ) : (
    <span className={triggerClassName}>{badge}</span>
  )

  return (
    <Tooltip>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent>
        <div className="space-y-1">
          <div>{getPriceAdjustmentActionLabel(adjustment)}</div>
          <div>{scheduleLabel}.</div>
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

function ProductPriceContent({ product }) {
  const priceRange = getProductPriceRange(product)
  const adjustment = getActivePriceAdjustment(product)

  return (
    <div className="flex min-w-0 flex-col items-start gap-1 lg:items-end">
      <span className="whitespace-nowrap font-semibold tabular-nums">
        {priceRange ?? 'No price'}
      </span>
      <ProductPriceChangeBadge adjustment={adjustment} />
    </div>
  )
}

export function getProductIndexColumns() {
  return [
    {
      id: 'product',
      header: 'Product',
      cell: ({ row }) => {
        const product = row.original

        return (
          <div className="flex min-w-0 items-center gap-3">
            <ImageThumbnail
              media={product.media?.[0]}
              className="h-12 w-12 rounded-md shadow-none"
            />
            <div className="min-w-0">
              <Typography
                variant="body"
                weight="semibold"
                interaction="hoverUnderline"
                className="block line-clamp-2 [overflow-wrap:anywhere]"
              >
                {product.name}
              </Typography>
              <ProductMarkerBadges product={product} />
            </div>
          </div>
        )
      },
      meta: {
        label: 'Product',
        kind: 'primary',
        icon: Flower2,
        sortable: productIndexSortColumns.product,
        width: '22rem',
        minWidth: '16rem',
        exportValue: (product) => product.name,
      },
    },
    {
      id: 'collections',
      header: 'Collections',
      cell: ({ row }) => <ProductCollectionsContent product={row.original} />,
      meta: {
        label: 'Collections',
        kind: 'text',
        icon: LibraryBig,
        width: '14rem',
        minWidth: '10rem',
        exportValue: getCollectionSummary,
      },
    },
    {
      id: 'variants',
      header: 'Variants',
      cell: ({ row }) => (
        <span className="whitespace-nowrap font-semibold tabular-nums">
          {getVariantCount(row.original)}
        </span>
      ),
      meta: {
        label: 'Variants',
        kind: 'number',
        icon: Layers3,
        sortable: productIndexSortColumns.variants,
        align: 'center',
        width: '6.5rem',
        minWidth: '5.25rem',
        exportValue: getVariantCount,
        exportType: 'integer',
      },
    },
    {
      id: 'price',
      header: 'Price',
      cell: ({ row }) => <ProductPriceContent product={row.original} />,
      meta: {
        label: 'Price',
        kind: 'money',
        icon: DollarSign,
        align: 'end',
        link: 'overlay',
        width: '10.5rem',
        minWidth: '9rem',
        exportValue: (product) => getProductPriceRange(product) ?? '',
      },
    },
    {
      id: 'storefront_visibility',
      header: 'Visible on website',
      cell: ({ row }) => (
        <div className="flex w-full items-center gap-2">
          <ProductStorefrontVisibilityBadge product={row.original} stackClassName="items-start" />
        </div>
      ),
      meta: {
        label: 'Visible on website',
        kind: 'badge',
        icon: Eye,
        sortable: productIndexSortColumns.storefrontVisibility,
        width: '13rem',
        minWidth: '11rem',
        exportValue: (product) =>
          product.storefront_visibility
            ? getEnumDisplayName(ProductStorefrontVisibility, product.storefront_visibility)
            : 'Unknown',
      },
    },
    {
      id: 'updated_at',
      header: 'Updated',
      cell: ({ row }) => (
        <span className="whitespace-nowrap tabular-nums">
          {dayjs(row.original.updated_at).format('MM/DD/YYYY')}
        </span>
      ),
      meta: {
        label: 'Updated',
        kind: 'date',
        icon: CalendarClock,
        sortable: productIndexSortColumns.updated,
        align: 'center',
        width: '8rem',
        minWidth: '7rem',
        exportValue: (product) => product.updated_at,
        exportType: 'date',
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => <ProductActionsContent product={row.original} />,
      meta: {
        label: 'Actions',
        kind: 'actions',
        link: false,
        align: 'end',
        cellClassName: 'lg:w-10',
        width: '2.5rem',
        exportable: false,
      },
    },
  ]
}

function ProductsDeleteAction() {
  const mutation = useSoftDeleteProductMutation()
  const onMutate = {
    onSuccess: () => {
      toast.success('Product deleted successfully')
    },
    onError: (e) => {
      toast.error('Error deleting product', { description: getErrorMessage(e) })
    },
  }

  return (
    <DeleteActionWithConfirmation
      model="product"
      onConfirm={(row) => mutation.mutateAsync(row?.original?.id, onMutate)}
      isPending={mutation.isPending}
    />
  )
}
